import React, { Component } from "react";

import {
    MDBBtn,
    MDBRow,
    MDBCol,
    MDBIcon,
    MDBModal,
    MDBModalHeader,
    MDBModalBody,
    MDBModalFooter,
    toast,
} from "mdbreact";

import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import Box from "@mui/material/Box";

import JSONPretty from "react-json-pretty";

import { withTranslation } from "react-i18next";
import { DateInput } from "semantic-ui-calendar-react";

import axios from "../../../axios";

import {
    getFunctionNameLabel,
    getFunctionNameOptions,
} from "../../../components/NetworkMgmt/Functions/logFunctionNames";

function QuickSearchToolbar() {
    return (
        <Box sx={{ p: 0.5, pb: 0 }}>
            <GridToolbarQuickFilter />
        </Box>
    );
}

// Number of log entries requested per page from the API.
const PAGE_SIZE = 10;

// Shared log viewer that talks to the unified /operatorconsole/listLogs
// endpoint. Restricts scope based on props (logType + identifier), supports
// filtering by function_name and by min/max date, and supports pagination via
// the API's searchBefore cursor.
//
// Props:
//   logType: "operator" | "venue" | "subscriber" | "unit" | "equipment"
//   venueID / subscriberToken / unitIDInternal / equipmentID: scope identifiers
class LogViewer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            logs: [],
            loading: false,
            selectedFunctionName: "",
            dateStart: "",
            dateEnd: "",
            // Pagination: stack of searchBefore cursors for previous pages, and
            // the cursor to fetch the next page.
            searchBeforeStack: [],
            nextSearchBefore: undefined,
            pageIndex: 0,
            modalDetail: false,
            detailLog: undefined,
        };
    }

    componentDidMount() {
        this.getLogs();
    }

    componentDidUpdate(prevProps) {
        const scopeChanged =
            prevProps.venueID !== this.props.venueID ||
            prevProps.subscriberToken !== this.props.subscriberToken ||
            prevProps.unitIDInternal !== this.props.unitIDInternal ||
            prevProps.equipmentID !== this.props.equipmentID;
        if (scopeChanged) {
            this.setState(
                {
                    logs: [],
                    selectedFunctionName: "",
                    dateStart: "",
                    dateEnd: "",
                    searchBeforeStack: [],
                    nextSearchBefore: undefined,
                    pageIndex: 0,
                },
                () => this.getLogs()
            );
        }
    }

    // Builds the scope-specific portion of the request body.
    getScopeBody = () => {
        switch (this.props.logType) {
            case "venue":
                return { VenueID: this.props.venueID };
            case "subscriber":
                return { SubscriberToken: this.props.subscriberToken };
            case "unit":
                return { UnitID_Internal: this.props.unitIDInternal };
            case "equipment":
                return { EquipmentID: this.props.equipmentID };
            case "operator":
            default:
                return {};
        }
    };

    getLogs = (searchBefore) => {
        this.setState({ loading: true });

        const body = {
            AccessToken: sessionStorage.getItem("AccessToken"),
            IdToken: sessionStorage.getItem("IdToken"),
            pageSize: PAGE_SIZE,
            ...this.getScopeBody(),
        };

        if (this.state.selectedFunctionName) {
            body.function_name = this.state.selectedFunctionName;
        }
        if (this.state.dateStart) {
            body["MinViewWindowDateUTC.ISO8601"] = new Date(
                this.state.dateStart
            ).toISOString();
        }
        if (this.state.dateEnd) {
            body["MaxViewWindowDateUTC.ISO8601"] = new Date(
                this.state.dateEnd
            ).toISOString();
        }
        if (searchBefore !== undefined) {
            body.searchBefore = searchBefore;
        }

        axios
            .post("/operatorconsole/listLogs", body, { headers: {} })
            .then((response) => {
                this.setState({
                    logs: response.data.log || [],
                    nextSearchBefore: response.data.searchBefore,
                    loading: false,
                });
            })
            .catch((error) => {
                toast.error(
                    error?.response?.data?.message ||
                        "There was an error loading logs!",
                    { closeButton: false }
                );
                this.setState({ loading: false });
            });
    };

    // Applies the current filter selections and resets pagination.
    applyFilters = () => {
        this.setState(
            {
                searchBeforeStack: [],
                pageIndex: 0,
            },
            () => this.getLogs()
        );
    };

    clearFilters = () => {
        this.setState(
            {
                selectedFunctionName: "",
                dateStart: "",
                dateEnd: "",
                searchBeforeStack: [],
                pageIndex: 0,
            },
            () => this.getLogs()
        );
    };

    handleFunctionNameChange = (event) => {
        this.setState({ selectedFunctionName: event.target.value });
    };

    changeHandler = (event, data) => {
        if (data && this.state.hasOwnProperty(data.name)) {
            this.setState({ [data.name]: data.value });
            return;
        }

        this.setState({ [event.target.name]: event.target.value });
    };

    nextPage = () => {
        if (this.state.nextSearchBefore === undefined) return;
        const cursor = this.state.nextSearchBefore;
        this.setState(
            (prev) => ({
                searchBeforeStack: [...prev.searchBeforeStack, cursor],
                pageIndex: prev.pageIndex + 1,
            }),
            () => this.getLogs(cursor)
        );
    };

    prevPage = () => {
        if (this.state.pageIndex === 0) return;
        this.setState(
            (prev) => {
                const stack = [...prev.searchBeforeStack];
                stack.pop(); // remove current page cursor
                return {
                    searchBeforeStack: stack,
                    pageIndex: prev.pageIndex - 1,
                };
            },
            () => {
                // The cursor for the (now current) page is the last remaining
                // one on the stack, or undefined for the first page.
                const stack = this.state.searchBeforeStack;
                const cursor =
                    stack.length > 0 ? stack[stack.length - 1] : undefined;
                this.getLogs(cursor);
            }
        );
    };

    toggleDetail = (log) => {
        this.setState({
            modalDetail: !this.state.modalDetail,
            detailLog: log,
        });
    };

    // Merges the known function_name options with any discovered in the current
    // result set so users can filter by names present in their data.
    getFunctionNameSelectOptions = () => {
        const options = getFunctionNameOptions();
        const known = new Set(options.map((o) => o.value));
        (this.state.logs || []).forEach((log) => {
            const fn = log && log.function_name;
            if (fn && !known.has(fn)) {
                known.add(fn);
                options.push({ value: fn, name: getFunctionNameLabel(fn) });
            }
        });
        return options.sort((a, b) => a.name.localeCompare(b.name));
    };

    buildRows = () => {
        return (this.state.logs || []).map((log, index) => {
            const dateRaw = log["@timestamp"];
            let dateFormatted = "";
            if (dateRaw) {
                try {
                    dateFormatted = Intl.DateTimeFormat("en-US", {
                        year: "numeric",
                        month: "numeric",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                    }).format(new Date(dateRaw));
                } catch (e) {
                    dateFormatted = dateRaw;
                }
            }
            const eventValue = log?.event;
            let description = "";
            if (eventValue !== undefined && eventValue !== null) {
                description =
                    typeof eventValue === "object"
                        ? JSON.stringify(eventValue)
                        : String(eventValue);
            }
            return {
                id: index,
                date: dateFormatted,
                functionName: getFunctionNameLabel(log.function_name),
                description: description,
                administrator: log.Administrator || "",
                sourceIP: log.sourceIP || "",
                raw: log,
            };
        });
    };

    render() {
        const rows = this.buildRows();
        const functionNameOptions = this.getFunctionNameSelectOptions();

        const columns = [
            { headerName: "Date", field: "date", flex: 0.2, minWidth: 160 },
            {
                headerName: "Function",
                field: "functionName",
                flex: 0.2,
                minWidth: 180,
            },
            {
                headerName: "Description",
                field: "description",
                flex: 0.4,
                minWidth: 240,
            },
            {
                headerName: "Administrator",
                field: "administrator",
                flex: 0.2,
                minWidth: 160,
            },
        ];

        return (
            <div className="logList">
                {/* Filters */}
                <MDBRow className="mb-2">
                    <MDBCol sm="12" md="4">
                        <label className="black-text">Function</label>
                        <select
                            className="form-control"
                            value={this.state.selectedFunctionName}
                            onChange={this.handleFunctionNameChange}
                        >
                            <option value="">All Functions</option>
                            {functionNameOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.name}
                                </option>
                            ))}
                        </select>
                    </MDBCol>
                    <MDBCol sm="12" md="3">
                        <label className="black-text">From Date</label>
                        <DateInput
                            dateFormat="MM/DD/YYYY"
                            name="dateStart"
                            placeholder="MM/DD/YYYY"
                            value={this.state.dateStart}
                            iconPosition="left"
                            onChange={this.changeHandler}
                            className="dateTimePicker"
                            closable={true}
                        />
                    </MDBCol>
                    <MDBCol sm="12" md="3">
                        <label className="black-text">To Date</label>
                        <DateInput
                            dateFormat="MM/DD/YYYY"
                            name="dateEnd"
                            placeholder="MM/DD/YYYY"
                            value={this.state.dateEnd}
                            iconPosition="left"
                            onChange={this.changeHandler}
                            className="dateTimePicker"
                            minDate={this.state.dateStart}
                            initialDate={this.state.dateStart}
                            closable={true}
                        />
                    </MDBCol>
                    <MDBCol
                        sm="12"
                        md="2"
                        className="d-flex align-items-end"
                    >
                        <MDBBtn
                            size="sm"
                            color="danger"
                            onClick={this.applyFilters}
                            disabled={this.state.loading}
                        >
                            <MDBIcon icon="filter" className="mr-1" />
                            Apply
                        </MDBBtn>
                        <MDBBtn
                            size="sm"
                            color="primary"
                            onClick={this.clearFilters}
                            disabled={this.state.loading}
                        >
                            Clear
                        </MDBBtn>
                    </MDBCol>
                </MDBRow>

                {/* Log table */}
                <div style={{ width: "100%" }}>
                    <DataGrid
                        autoHeight
                        rows={rows}
                        columns={columns}
                        loading={this.state.loading}
                        hideFooterPagination={true}
                        hideFooter={true}
                        disableRowSelectionOnClick={true}
                        onRowClick={(params) => this.toggleDetail(params.row.raw)}
                        sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
                        slots={{ toolbar: QuickSearchToolbar }}
                        localeText={{ noRowsLabel: "No Logs Found" }}
                    />
                </div>

                {/* Pagination */}
                <div className="d-flex justify-content-between align-items-center mt-2">
                    <MDBBtn
                        size="sm"
                        color="primary"
                        onClick={this.prevPage}
                        disabled={this.state.pageIndex === 0 || this.state.loading}
                    >
                        <MDBIcon icon="chevron-left" className="mr-1" />
                        Previous
                    </MDBBtn>
                    <span className="text-muted">
                        Page {this.state.pageIndex + 1}
                    </span>
                    <MDBBtn
                        size="sm"
                        color="primary"
                        onClick={this.nextPage}
                        disabled={
                            this.state.nextSearchBefore === undefined ||
                            this.state.loading ||
                            rows.length === 0
                        }
                    >
                        Next
                        <MDBIcon icon="chevron-right" className="ml-1" />
                    </MDBBtn>
                </div>

                {/* Detail modal (individual JSON) */}
                <MDBModal
                    isOpen={this.state.modalDetail}
                    toggle={() => this.toggleDetail(undefined)}
                    size="lg"
                >
                    <MDBModalHeader toggle={() => this.toggleDetail(undefined)}>
                        Log Detail
                    </MDBModalHeader>
                    <MDBModalBody>
                        {this.state.detailLog !== undefined ? (
                            <>
                                <div className="mb-2">
                                    <MDBBtn
                                        size="sm"
                                        color="primary"
                                        onClick={() => {
                                            navigator.clipboard.writeText(
                                                JSON.stringify(
                                                    this.state.detailLog,
                                                    null,
                                                    2
                                                )
                                            );
                                            toast.success(
                                                "JSON copied to clipboard!",
                                                { closeButton: false }
                                            );
                                        }}
                                    >
                                        <MDBIcon icon="copy" className="mr-1" />
                                        Copy JSON
                                    </MDBBtn>
                                </div>
                                <JSONPretty
                                    id="log-detail-json"
                                    data={this.state.detailLog}
                                    theme={{
                                        main: "line-height:1.3;color:#66d9ef;overflow:auto;",
                                        error: "line-height:1.3;color:#66d9ef;overflow:auto;",
                                        key: "color:#f92672;",
                                        string: "color:#a6e22e;",
                                        value: "color:#ae81ff;",
                                        boolean: "color:#ac81fe;",
                                    }}
                                    style={{
                                        whiteSpace: "pre",
                                        fontSize: "12px",
                                        fontFamily:
                                            'Monaco, Menlo, "Ubuntu Mono", monospace',
                                        border: "1px solid #ddd",
                                        borderRadius: "4px",
                                        padding: "15px",
                                        backgroundColor: "#f8f9fa",
                                    }}
                                    space={2}
                                />
                            </>
                        ) : null}
                    </MDBModalBody>
                    <MDBModalFooter>
                        <MDBBtn
                            color="primary"
                            onClick={() => this.toggleDetail(undefined)}
                        >
                            Close
                        </MDBBtn>
                    </MDBModalFooter>
                </MDBModal>
            </div>
        );
    }
}

export default withTranslation()(LogViewer);
